<template>
    <div class="payment-result-container" style="direction: rtl;">
        <div v-if="loading" class="card state-card loading">
            <div class="state-icon">...</div>
            <h2>در حال بررسی وضعیت پرداخت</h2>
        </div>

        <div v-else-if="error" class="card state-card error">
            <div class="state-icon">!</div>
            <h2>خطا در دریافت اطلاعات</h2>
            <p class="state-text">{{ error }}</p>
            <button class="home-btn" @click="goHome">بازگشت به صفحه اصلی</button>
        </div>

        <div v-else class="card result-card" :class="paymentData.isSuccess ? 'success' : 'failed'">
            <div class="result-header">
                <div class="state-icon">{{ paymentData.isSuccess ? '✓' : '✕' }}</div>
                <h2>{{ paymentData.isSuccess ? 'پرداخت موفق' : 'پرداخت ناموفق' }}</h2>
            </div>

            <div class="info-list">
                <div class="info-item">
                    <span class="label">شماره فاکتور</span>
                    <span class="value">{{ paymentData.invoiceId || '-' }}</span>
                </div>
                <div class="info-item">
                    <span class="label">شماره پیگیری</span>
                    <span class="value">{{ paymentData.referenceNumber || '-' }}</span>
                </div>
                <div class="info-item">
                    <span class="label">پیام</span>
                    <span class="value">{{ paymentData.message || '-' }}</span>
                </div>
                <div class="info-item amount">
                    <span class="label">مبلغ</span>
                    <span class="value">{{ formatPrice(paymentData.amount) }} تومان</span>
                </div>
            </div>

            <button class="home-btn" @click="goHome">بازگشت به صفحه اصلی</button>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { usePepTransactionStore } from '@/stores/common/pepTransaction'

const route = useRoute()
const router = useRouter()

const invoiceId = route.params.invoiceId

const loading = ref(true)
const error = ref(null)
const paymentData = ref({})
const pepTransaction = usePepTransactionStore()

const fetchPaymentResult = async () => {
    try {
        const response = await pepTransaction.fetchPaymentResult(invoiceId)
        paymentData.value = response
    } catch (err) {
        error.value = 'امکان دریافت اطلاعات پرداخت وجود ندارد.'
    } finally {
        loading.value = false
    }
}

const formatPrice = (value) => {
    if (!value) return '0'
    return new Intl.NumberFormat('fa-IR').format(value)
}

const goHome = () => {
    router.push('/overview/common/user-area')
}

onMounted(() => {
    fetchPaymentResult()
})
</script>

<style scoped>
.payment-result-container {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: calc(100vh - 140px);
    padding: 24px;
    background:
        radial-gradient(circle at top right, rgba(20, 184, 166, 0.08), transparent 55%),
        radial-gradient(circle at bottom left, rgba(59, 130, 246, 0.08), transparent 60%),
        #f4f7fb;
}

.card {
    width: min(100%, 520px);
    padding: 28px 24px;
    border-radius: 16px;
    background: #fff;
    box-shadow: 0 14px 30px rgba(12, 24, 48, 0.1);
    border: 1px solid #eef2f7;
}

.state-card {
    text-align: center;
}

.result-card {
    border-top: 6px solid transparent;
}

.success {
    border-top-color: #10b981;
}

.failed {
    border-top-color: #ef4444;
}

.error {
    border-top: 6px solid #f59e0b;
}

.result-header {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 18px;
}

.result-header h2,
.state-card h2 {
    margin: 0;
    color: #1f2a37;
    font-size: 1.3rem;
    font-weight: 700;
}

.state-icon {
    width: 42px;
    height: 42px;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-weight: 800;
    font-size: 1rem;
    color: #0f172a;
    background: #e2e8f0;
}

.success .state-icon {
    color: #065f46;
    background: #d1fae5;
}

.failed .state-icon {
    color: #7f1d1d;
    background: #fee2e2;
}

.error .state-icon {
    color: #78350f;
    background: #fef3c7;
}

.loading .state-icon {
    color: #1e3a8a;
    background: #dbeafe;
}

.state-text {
    margin: 12px 0 0;
    color: #4b5563;
}

.info-list {
    display: grid;
    gap: 10px;
    margin-top: 6px;
}

.info-item {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    align-items: center;
    background: #f8fafc;
    border: 1px solid #edf2f7;
    border-radius: 10px;
    padding: 10px 12px;
}

.info-item .label {
    color: #475569;
    font-size: 0.92rem;
}

.info-item .value {
    color: #0f172a;
    font-weight: 700;
    text-align: left;
    max-width: 62%;
    word-break: break-word;
}

.info-item.amount {
    background: #ecfeff;
    border-color: #bae6fd;
}

.home-btn {
    width: 100%;
    margin-top: 18px;
    padding: 11px 14px;
    border: 0;
    border-radius: 10px;
    cursor: pointer;
    background: linear-gradient(135deg, #0ea5e9, #2563eb);
    color: #fff;
    font-size: 0.94rem;
    font-weight: 700;
    transition: transform 0.18s ease, box-shadow 0.18s ease;
}

.home-btn:hover {
    transform: translateY(-1px);
    box-shadow: 0 8px 20px rgba(37, 99, 235, 0.28);
}

@media (max-width: 600px) {
    .payment-result-container {
        padding: 14px;
        min-height: calc(100vh - 96px);
    }

    .card {
        padding: 20px 16px;
    }

    .result-header h2,
    .state-card h2 {
        font-size: 1.08rem;
    }

    .info-item {
        flex-direction: column;
        align-items: flex-start;
    }

    .info-item .value {
        max-width: 100%;
        text-align: right;
    }
}
</style>
